import React, { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { api, formatErr } from "../../../lib/api";
import Field from "../components/Field";
import { formatLocalDate } from "../utils/formatLocalDate";

/**
 * Holidays section — yearly closures + Italian-preset seeder.
 *
 * Distinct from `Blocks` (ad-hoc datetime ranges) and from
 * `HoursOverridesSection` (date-range schedule overrides).
 *
 * Extracted from BusinessDashboardShell.jsx (iter28 refactor step 2.6).
 * No logic, UX or API change — only the file location.
 *
 * Capability: read gated by `appointments:view` (so managers/staff can see
 * the schedule). Mutations gated by `legal:view`/owner-only on POST/DELETE
 * — see backend `routes/holidays.py`. UI gating is at the shell level.
 *
 * Rendered as a sibling section inside `WorkingHours.jsx`.
 */
export default function HolidaysSection({ businessId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [form, setForm] = useState({ date: "", date_to: "", label: "", recurring: false });
  const [err, setErr] = useState("");
  const [toast, setToast] = useState("");

  async function load() {
    setLoading(true);
    const { data } = await api.get(`/businesses/${businessId}/holidays`);
    setItems(data || []);
    setLoading(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [businessId]);

  async function add(e) {
    e.preventDefault();
    setErr("");
    try {
      await api.post(`/businesses/${businessId}/holidays`, {
        date: form.date,
        date_to: form.date_to || null,
        label: form.label.trim(),
        recurring: form.recurring,
      });
      setForm({ date: "", date_to: "", label: "", recurring: false });
      setAdding(false);
      load();
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Errore");
    }
  }
  async function del(id) {
    if (!window.confirm("Eliminare questa festività?")) return;
    await api.delete(`/businesses/${businessId}/holidays/${id}`);
    load();
  }
  async function seedItalian() {
    setSeeding(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/holidays/seed-italian`, { year });
      setToast(`✓ ${data.inserted} festività inserite${data.skipped ? `, ${data.skipped} già presenti` : ""}`);
      load();
      setTimeout(() => setToast(""), 4000);
    } finally {
      setSeeding(false);
    }
  }

  const sorted = [...items].sort((a, b) => (a.recurring === b.recurring ? a.date.localeCompare(b.date) : (a.recurring ? -1 : 1)));

  return (
    <div className="mt-10 pt-8 border-t border-zinc-200 dark:border-zinc-700" data-testid="holidays-section">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h4 className="font-display text-xl font-medium tracking-tight">Festività e chiusure</h4>
          <p className="text-sm text-zinc-500 mt-1 max-w-md dark:text-zinc-400">
            Giorni in cui l'attività è chiusa: i clienti non potranno prenotare. Puoi inserire singoli giorni, periodi (ferie) o festività ricorrenti annuali.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} className="h-10 rounded-full bg-zinc-100 px-3 text-sm dark:bg-zinc-800" data-testid="holidays-year">
            {[year - 1, year, year + 1, year + 2].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={seedItalian} disabled={seeding} className="h-10 px-4 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-800 text-sm font-medium dark:bg-emerald-950/40 dark:text-emerald-300" data-testid="holidays-seed">
            {seeding ? "Inserisco…" : "🇮🇹 Festività italiane"}
          </button>
          <button onClick={() => setAdding(true)} className="btn-accent h-10 px-4 text-sm" data-testid="holidays-add-btn">+ Aggiungi</button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 dark:text-zinc-400">Caricamento…</div>
      ) : sorted.length === 0 ? (
        <div className="card-soft p-6 text-center text-sm text-zinc-500 dark:text-zinc-400" data-testid="holidays-empty">
          Nessuna festività configurata. Tap su "🇮🇹 Festività italiane" per aggiungere automaticamente quelle nazionali per il {year}.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {sorted.map((h) => (
            <div key={h.id} className={`flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border ${h.recurring ? "border-emerald-200 bg-emerald-50/50 dark:border-emerald-800/50 dark:bg-emerald-950/40" : "border-zinc-200 bg-white"}`} data-testid={`holiday-row-${h.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{h.label}</div>
                <div className="text-xs text-zinc-500 mt-0.5 dark:text-zinc-400">
                  {h.recurring ? <span className="font-semibold text-emerald-700 dark:text-emerald-300">Ricorrente</span> : null}
                  {h.recurring && " · "}
                  {h.date_to ? `${formatLocalDate(h.date)} → ${formatLocalDate(h.date_to)}` : formatLocalDate(h.date)}
                  {h.is_preset && <span className="ml-2 text-[10px] text-zinc-400 uppercase tracking-wider dark:text-zinc-500">Preset</span>}
                </div>
              </div>
              <button onClick={() => del(h.id)} className="h-8 w-8 rounded-full bg-rose-50 text-rose-600 hover:bg-rose-100 flex items-center justify-center dark:bg-rose-950/40" data-testid={`holiday-del-${h.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {adding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setAdding(false)} data-testid="holiday-add-modal">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 dark:bg-zinc-900" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={add} className="space-y-4">
              <div>
                <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600">Festività</div>
                <h3 className="font-display text-2xl font-medium mt-1">Aggiungi chiusura</h3>
              </div>
              <Field label="Etichetta (es. 'Ferie estive', 'Compleanno')">
                <input required maxLength={120} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="input-soft" data-testid="holiday-label" />
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Dal">
                  <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-soft" data-testid="holiday-date" />
                </Field>
                <Field label="Al (opzionale)">
                  <input type="date" value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })} className="input-soft" data-testid="holiday-date-to" />
                </Field>
              </div>
              <label className="flex items-start gap-2 cursor-pointer text-sm">
                <input type="checkbox" checked={form.recurring} onChange={(e) => setForm({ ...form, recurring: e.target.checked })} className="mt-0.5 h-4 w-4 accent-blue-600" disabled={!!form.date_to} data-testid="holiday-recurring" />
                <span>
                  <strong>Ripeti ogni anno</strong>
                  <span className="block text-xs text-zinc-500 mt-0.5 dark:text-zinc-400">Per festività con data fissa (Natale, compleanno). Non disponibile per i periodi.</span>
                </span>
              </label>
              {err && <div className="text-xs text-rose-700 dark:text-rose-300">{err}</div>}
              <button type="submit" className="btn-accent w-full" data-testid="holiday-submit">Aggiungi</button>
            </form>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-emerald-600 text-white text-sm px-4 py-2.5 rounded-full shadow-lg" data-testid="holidays-toast">
          {toast}
        </div>
      )}
    </div>
  );
}
